import { makeStyles } from '@material-ui/core';

// Vocabulary matched to the rest of Project Development (GanttChart/Styles.js,
// ProjectAnalytics/Styles.js): #173072 brand navy, #ec6535 brand orange,
// neutral greys for borders and secondary text.
const BORDER = '#E4E7EC';
const HEADING = '#101828';
const MUTED = '#667085';

// Every campaign table shares one column template so the columns line up down
// the whole page instead of each table sizing itself to its own longest stage
// name — the single biggest thing that made the first cut look unfinished.
export const STAGE_COL_WIDTHS = ['34%', '16.5%', '16.5%', '16.5%', '16.5%'];

export const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3, 4),
    fontFamily: theme.typography.fontFamily,
    color: HEADING,
  },

  // ── screen header ─────────────────────────────────────────────────────────
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: theme.spacing(2), marginBottom: theme.spacing(2),
  },
  title: { fontSize: '1.25rem', fontWeight: 600, color: '#173072', whiteSpace: 'nowrap' },
  backButton: {
    display: 'flex', alignItems: 'center', cursor: 'pointer', color: HEADING,
    '&:hover': { opacity: 0.75 },
  },

  // ── filter bar ────────────────────────────────────────────────────────────
  filterRow: {
    display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap',
    gap: theme.spacing(2.5), paddingBottom: theme.spacing(2.5),
    borderBottom: `1px solid ${BORDER}`, marginBottom: theme.spacing(2.5),
  },
  fieldLabel: {
    fontSize: '0.75rem', fontWeight: 600, color: '#344054', marginBottom: 6,
  },
  field: { minWidth: 230 },
  // The collapsed dropdown shows the placeholder in grey until something is
  // checked, matching the "Select CMO…" state on the empty screen.
  placeholder: { color: '#98A2B3' },
  filterActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  searchButton: {
    backgroundColor: '#ec6535', color: '#fff', textTransform: 'none',
    borderRadius: '2rem', minWidth: 116, height: 38, padding: '6px 22px',
    fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
    '&:hover': { backgroundColor: '#000', color: '#fff' },
    '&.Mui-disabled': { backgroundColor: '#EAECF0', color: '#98A2B3' },
  },
  reportButton: {
    backgroundImage: 'linear-gradient(to right, #301232, #1A2D6C, #173072)',
    color: '#fff', textTransform: 'none', borderRadius: '2rem',
    minWidth: 158, height: 38, padding: '6px 22px',
    fontSize: '0.875rem', fontWeight: 600, boxShadow: 'none',
    '&:hover': { backgroundImage: 'none', backgroundColor: '#000' },
    '&.Mui-disabled': { backgroundImage: 'none', backgroundColor: '#EAECF0', color: '#98A2B3' },
  },

  // ── last synced / refresh ─────────────────────────────────────────────────
  // One per screen (FSD §5), sitting above the section list.
  syncRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    gap: theme.spacing(0.5), marginBottom: theme.spacing(1),
  },
  syncText: { fontSize: '0.75rem', color: MUTED, whiteSpace: 'nowrap' },
  refreshButton: {
    textTransform: 'none', fontSize: '0.75rem', fontStyle: 'italic',
    color: '#ec6535', minWidth: 0, padding: '2px 8px',
  },

  // ── collapsible sections ──────────────────────────────────────────────────
  section: { marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: theme.spacing(1),
    cursor: 'pointer', background: 'none', border: 'none', padding: 0,
    font: 'inherit', color: HEADING, textAlign: 'left',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '0.01em' },
  sectionBody: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },

  // ── one campaign card ─────────────────────────────────────────────────────
  // The card is what separates one campaign from the next; without it the
  // stacked meta rows and tables ran together down the page.
  campaignCard: {
    display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1),
    border: `1px solid ${BORDER}`, borderRadius: 8, background: '#fff',
    padding: theme.spacing(2, 2.5), maxWidth: 1180,
  },
  campaignChevron: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, flexShrink: 0, marginTop: -2,
    background: 'none', border: 'none', borderRadius: 4, padding: 0,
    cursor: 'pointer', color: '#475467',
    '&:hover': { background: '#F2F4F7' },
  },
  campaignBody: { flex: 1, minWidth: 0 },

  // Five labelled facts laid on an auto-fitting grid so a long CMO name gets a
  // column of its own instead of crowding the ones beside it.
  campaignMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: theme.spacing(1.5, 3),
  },
  metaLabel: {
    fontSize: '0.6875rem', fontWeight: 600, color: MUTED,
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2,
  },
  metaValue: { fontSize: '0.8125rem', fontWeight: 500, color: HEADING, lineHeight: 1.4 },
  statusPill: {
    display: 'inline-block', fontSize: '0.75rem', fontWeight: 600,
    borderRadius: 12, padding: '2px 10px', lineHeight: 1.5,
  },
  statusOngoing: { background: '#FFF4E5', color: '#B54708' },
  statusCompleted: { background: '#E7F6EC', color: '#067647' },

  // ── stage table ───────────────────────────────────────────────────────────
  tableWrap: {
    marginTop: theme.spacing(2), border: `1px solid ${BORDER}`, borderRadius: 6,
    overflowX: 'auto',
  },
  table: { tableLayout: 'fixed', minWidth: 680 },
  headCell: {
    fontSize: '0.75rem', fontWeight: 600, color: '#344054',
    background: '#F9FAFB', borderBottom: `1px solid ${BORDER}`,
    padding: '11px 16px', whiteSpace: 'nowrap',
  },
  bodyCell: {
    fontSize: '0.8125rem', color: '#344054',
    borderBottom: '1px solid #F2F4F7', padding: '13px 16px',
  },
  // Last row sits on the wrapper's own border; a second line looks like a gap.
  lastRowCell: { borderBottom: 'none' },
  stageCell: { fontWeight: 500, color: HEADING },
  completionCell: { fontWeight: 600, color: HEADING },
  // A dash is absence of data, not a value — it should recede, not read as 0.
  dashCell: { color: '#98A2B3' },

  // ── notes / empty states ──────────────────────────────────────────────────
  cardNote: {
    fontSize: '0.75rem', color: MUTED, marginTop: theme.spacing(1.25),
    fontStyle: 'italic',
  },
  centered: { display: 'flex', justifyContent: 'center', padding: theme.spacing(8, 0) },
  emptyPane: {
    border: `1px dashed ${BORDER}`, borderRadius: 8, background: '#FCFCFD',
    padding: theme.spacing(5, 3), textAlign: 'center', maxWidth: 1180,
  },
  emptyText: { fontSize: '0.875rem', color: MUTED },
}));
